import { useMemo } from 'react';
import type { PlacemarkI } from '@components/MapRender';
import { buildShopTypePlacemarkOptions } from '@components/MapRender/placemarks';
import { createPlacemarkWithQuantityOptions } from '../ReliablePlacemark';
import { getStoreAvailableQuantity } from '../utils';
import type { AvailableQuantityItem, StoreListEntry } from '../types';
import { flatMapArray } from '@utils/flatMapArray';

interface UseStorePlacemarksParams {
  stores: StoreListEntry[];
  activeStoreId: number;
  onSelect: (id: number) => void;
  isShowAvailableQuantity?: boolean;
  availableQuantity?: AvailableQuantityItem[];
}

const useStorePlacemarks = ({
  stores,
  activeStoreId,
  onSelect,
  isShowAvailableQuantity,
  availableQuantity
}: UseStorePlacemarksParams): PlacemarkI[] =>
  useMemo(
    () =>
      flatMapArray(stores, item => {
        const geometry = item.geo;
        if (!Array.isArray(geometry) || geometry.length < 2) {
          return [];
        }

        const isActiveItem = item.id === activeStoreId;
        const baseHandlers = {
          geometry,
          onClick: () => {
            if (item.id) onSelect(item.id);
          }
        };

        if (isShowAvailableQuantity) {
          const quantity = getStoreAvailableQuantity(
            item.id,
            availableQuantity
          );
          return [
            {
              ...baseHandlers,
              options: createPlacemarkWithQuantityOptions(
                item,
                isActiveItem,
                quantity || 0
              )
            }
          ];
        }

        return [
          {
            ...baseHandlers,
            options: buildShopTypePlacemarkOptions(
              item.type === 'cafe',
              !!item.opens_soon
            )
          }
        ];
      }),
    [
      stores,
      activeStoreId,
      isShowAvailableQuantity,
      availableQuantity,
      onSelect
    ]
  );

export default useStorePlacemarks;
